import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { FileUploadService } from './services/file-upload.service';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MobiDropZoneDirective } from './directives/mobi-drop-zone.directive';
import { MatProgressBar } from '@angular/material/progress-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, concatMap, delay, finalize, takeUntil, tap } from 'rxjs/operators';
import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { MatIconButton } from '@angular/material/button';
import { FILE_EXTENSIONS_CONST, FileUploadItem, MIME_TYPES_CONST, MimeTypes, StatusType, UploadCompleteEvent, UploadFactory } from './models/file-uploader';


/**
 * A reusable file uploader component that supports drag-and-drop, multiple files,
 * automatic/manual uploads, and validation for file type and size.
 *
 * @example
 * <mobi-file-uploader
 *   [fileTypes]="['pdf', 'png']"
 *   [maxFileSize]="5"
 *   [maxFiles]="3"
 *   [uploadRequestFactory]="buildUploadRequest"
 *   [initialFiles]="persistedFiles()"
 *   (filesChanged)="onFilesChanged($event)"
 *   (uploadComplete)="onUploadComplete($event)"
 * />
 */
@Component({
  selector: 'mobi-file-uploader',
  imports: [MobiDropZoneDirective, MatIcon, TranslateModule, MatProgressBar, MatIconButton],
  templateUrl: './mobi-file-uploader.component.html',
  styleUrl: './mobi-file-uploader.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [FileUploadService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobiFileUploaderComponent implements OnInit {
  /** Reference to the hidden file input element. */
  private fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  /** List of allowed file extensions (e.g., 'pdf', 'png', 'xls'). Defaults to ['pdf']. */
  public readonly fileTypes = input<readonly MimeTypes[]>(['pdf']);
  /** The endpoint URL where files will be uploaded. */
  public readonly uploadUrl = input<string>('');
  /** Whether to start uploading files immediately upon selection/drop. Defaults to false. */
  public readonly autoUpload = input<boolean>(false);
  /** Maximum allowed file size in Megabytes. Defaults to 1MB. */
  public readonly maxFileSize = input<number>(1); // MB
  /** Maximum number of files allowed in the list. Defaults to 1. */
  public readonly maxFiles = input<number>(1);
  /** Whether to keep showing files in the list after they are successfully uploaded. Defaults to true. */
  public readonly showUploadedFiles = input<boolean>(true);
  /** Whether the uploader interaction is disabled. Defaults to false. */
  public readonly disableUpload = input<boolean>(false);
  /** The key used for the file in the FormData object. Defaults to 'document'. */
  public readonly fileKey = input<string>('document');

  /**
   * Optional list of files to seed the uploader with on initialisation.
   * Useful when the parent persists state across re-mounts and needs the
   * picked-but-not-yet-uploaded file to survive.
   *
   * Read once during `ngOnInit`; later changes to the bound value are
   * ignored, so the parent must provide the final seed list synchronously
   * before the component is rendered. Files in `initialFiles` bypass
   * validation and duplicate detection — the parent is trusted as the
   * source of truth.
   */
  public readonly initialFiles = input<File[]>([]);

  /** An optional factory function that builds the upload request for a given file. */
  public readonly uploadRequestFactory = input<UploadFactory>(( file ) => {
    const formData = new FormData();
    formData.append(this.fileKey(), file);

    return { url: this.uploadUrl(), formData: formData };
  });
  /** Emitted whenever the list of valid files changes. */
  public readonly filesChanged = output<File[]>();
  /** Emitted when all valid files in the current list have finished uploading. Contains error details if any files failed. */
  public readonly uploadComplete = output<UploadCompleteEvent>();

  /** Signal containing the list of all files (valid and invalid) currently managed by the component. */
  protected readonly fileItems = signal<FileUploadItem[]>([]);
  /** Computed string of allowed file types for display (e.g., "pdf, png"). */
  protected readonly fileTypesFormatted = computed(() => this.fileTypes().join(', '));
  /** Computed string for the 'accept' attribute of the file input. Includes both
   * canonical MIME values and known extensions per type so the OS picker filters
   * reliably regardless of how the browser reports MIME for a given file. */
  protected readonly acceptAttribute = computed(() =>
    this.fileTypes()
      .flatMap(type => [MIME_TYPES_CONST[type], ...FILE_EXTENSIONS_CONST[type]])
      .join(',')
  );
  /** Computed human-readable max file size string. */
  protected readonly maxFileSizeFormatted = computed(() => this.formatSize(this.maxFileSize()));
  /** Computed boolean indicating if multiple file selection is allowed. */
  protected readonly allowMultiple = computed(() => this.maxFiles() > 1);
  /** Signal to control visibility of the error messages box. */
  protected readonly showErrorFiles = signal<boolean>(true);
  /** Computed count of valid files. */
  protected readonly activeFilesCount = computed(() => this.validFiles().length);

  /** Computed list of files that do not have validation errors. */
  protected readonly validFiles = computed(() => this.fileItems().filter(file => file.errorType() !== 'validation'));
  /** Computed list of files that failed validation. */
  protected readonly errorFiles = computed(() => this.fileItems().filter(file => file.errorType() === 'validation'));
  /** Computed boolean indicating if there are any valid files in the list. */
  protected readonly hasFiles = computed(() => this.validFiles().length > 0);
  /** Computed boolean indicating if there are any files with validation errors. */
  protected readonly hasErrorFiles = computed(() => this.errorFiles().length > 0);
  /** Computed boolean indicating if any file is currently being uploaded. */
  protected readonly isUploading = computed(() => this.validFiles().some(file => file.status() === 'uploading'));

  /** Computed boolean indicating if all valid files have reached a terminal state (completed or error). */
  protected readonly uploadDone = computed(() => {
    const valid = this.validFiles();
    if (valid.length === 0) {return false;}
    return valid.every(file => file.status() === 'completed' || file.status() === 'error');
  });
  /** Mapping of file status to Material icon names. */
  protected readonly iconMap: Record<StatusType, string> = {
    pending: 'description',
    error: 'error',
    completed: 'check_circle',
    cancelled: 'description',
    uploading: 'description'
  };

  /** Internal queue for sequential file uploads. */
  private readonly uploadQueue$ = new Subject<FileUploadItem>();
  /** Service used to perform the HTTP upload requests. */
  private readonly fileUploadService = inject(FileUploadService);

  /**
   * Initializes the upload queue subscription.
   */
  constructor() {
    this.uploadQueue$.pipe(
      takeUntilDestroyed(),
      concatMap(item => this.performUpload(item))
    ).subscribe(() => {
      this.emitUploadComplete();
    });
  }

  public ngOnInit(): void {
    this.seedInitialFiles();
  }

  /**
   * Uploads all valid files that are currently in the pending, error, or cancelled state.
   */
  public uploadAllFiles(): void {
    this.validFiles().forEach(file => this.uploadQueue$.next(file));
  }

  /**
   * Triggers the hidden file input click event to open the file picker dialog.
   * Does nothing if the uploader is disabled.
   */
  protected triggerFilePicker() {
    if (this.disableUpload()) {return;}
    this.fileInput()?.nativeElement.click();
  }

  /**
   * Handles the selection of files via the native file picker.
   * @param files The list of selected files from the input event.
   */
  protected onFileSelected( files: FileList ) {
    this.prepareFiles(files);
    // Clear the file input so the same file can be selected again
    if (this.fileInput()?.nativeElement) {
      this.fileInput()!.nativeElement.value = '';
    }
  }

  /**
   * Handles files dropped onto the drop zone area.
   * @param files The list of dropped files from the drag-and-drop event.
   */
  protected onFileDropped( files: FileList ) {
    if (this.disableUpload()) {return;}
    this.prepareFiles(files);
  }

  /**
   * Removes a file from the list of valid files and cancels its upload if in progress.
   * @param index The index of the file to remove within the valid files list.
   */
  protected removeFile( index: number ) {
    const item = this.validFiles()[index];
    if (!item) {
      return;
    }
    this.cancelUpload(item);
    this.fileItems.update(uploadedFiles => uploadedFiles.filter(file => file !== item));
    this.emitFilesChanged();
  }

  /**
   * Cancels the upload of a specific file item.
   * Updates the status to 'cancelled' and triggers the cancellation subject if uploading.
   * @param item The file upload item to cancel.
   */
  protected cancelUpload( item: FileUploadItem ): void {
    if (item.status() === 'uploading') {
      item.cancel$?.next();
      item.progress.set(0);
      item.status.set('cancelled');
    } else if (item.status() === 'pending' || item.status() === 'error') {
      item.status.set('cancelled');
    }
  }

  /**
   * Closes the box displaying files with validation errors and clears them from the list.
   */
  protected closeErrorFilesBox(): void {
    this.showErrorFiles.set(false);
    this.fileItems.update(files => files.filter(file => file.errorType() !== 'validation'));
  }

  /**
   * Seeds the uploader with the files supplied via `initialFiles`. Runs once
   * from `ngOnInit`; later changes to the bound list are intentionally ignored.
   *
   * Returns early when no files are supplied so we don't emit a spurious
   * `(filesChanged)` event with an empty list during initialisation.
   */
  private seedInitialFiles(): void {
    const seed = this.initialFiles();
    if (seed.length === 0) {
      return;
    }
    const items: FileUploadItem[] = seed.map(file => ({
      file,
      progress: signal(0),
      status: signal('pending'),
      cancel$: new Subject<void>(),
      errorKeys: signal([]),
      errorType: signal(undefined),
      httpError: signal(undefined),
    }));
    this.fileItems.set(items);
    this.emitFilesChanged();
  }

  /**
   * Checks if a file is already present in the list of uploaded files based on name and size.
   * @param file The file to check for duplication.
   * @returns `true` if the file is a duplicate, `false` otherwise.
   */
  private isDuplicate( file: File ): boolean {
    return this.fileItems().some(
      fileItem => fileItem.file.name === file.name && fileItem.file.size === file.size
    );
  }

  /**
   * Validates a file against allowed size and MIME type constraints.
   * @param file The file to validate.
   * @returns An array of translation keys for the encountered validation errors.
   */
  private validateFile( file: File ): string[] {
    const maxSizeInBytes = this.maxFileSize() * 1024 * 1024;

    const errors: string[] = [];

    if (file.size === 0) {
      errors.push('FILE_UPLOAD_COMPONENT.ERROR_EMPTY_FILE');
    }

    if (file.size > maxSizeInBytes) {
      errors.push('FILE_UPLOAD_COMPONENT.ERROR_FILE_SIZE_EXCEEDED');
    }

    if (!this.isFileTypeAllowed(file)) {
      errors.push('FILE_UPLOAD_COMPONENT.ERROR_FILE_TYPE_NOT_ALLOWED');
    }

    return errors;
  }

  /**
   * Determines whether a file's type is permitted based on the configured `fileTypes`.
   * Accepts a file when either the browser-reported MIME matches the canonical
   * value for one of the configured types, OR the file name ends with an extension
   * registered for one of those types — the latter is the fallback for files that
   * browsers report under non-standard MIMEs (csv as `application/vnd.ms-excel`,
   * xlsx as `application/zip`, eml as `application/octet-stream`, etc.).
   * @param file The file to check.
   * @returns `true` if the file type is allowed.
   */
  private isFileTypeAllowed( file: File ): boolean {
    const lowerName = file.name.toLowerCase();
    return this.fileTypes().some(type =>
      file.type === MIME_TYPES_CONST[type] ||
      FILE_EXTENSIONS_CONST[type].some(ext => lowerName.endsWith(ext))
    );
  }

  /**
   * Prepares the selected or dropped files for addition, respecting the `maxFiles` limit.
   * If `maxFiles` is 1, it replaces the existing file. Otherwise, it adds up to the remaining capacity.
   * @param files The list of files to prepare for addition.
   */
  private prepareFiles( files: FileList ) {
    const fileArray = Array.from(files);

    if (!this.allowMultiple() && fileArray.length > 0) {
      this.fileItems.set([]);
      this.addFile(fileArray[0]!);
    } else {
      const remainingCount = this.maxFiles() - this.validFiles().length;
      fileArray.slice(0, remainingCount).forEach(file => this.addFile(file));
    }
  }

  /**
   * Adds a file to the component state, performs validation, and triggers auto-upload if enabled.
   * Skips duplicates.
   * @param file The file to add to the component state.
   */
  private addFile( file: File ): void {
    if (this.isDuplicate(file)) {
      return;
    }

    const errorsValidation = this.validateFile(file);
    const hasErrors = errorsValidation.length > 0;
    const fileUploadItem: FileUploadItem = {
      file: file,
      progress: signal(0),
      status: signal('pending'),
      cancel$: new Subject(),
      errorKeys: signal(errorsValidation),
      errorType: signal(hasErrors ? 'validation' : undefined),
      httpError: signal(undefined),
    };

    this.fileItems.update(current => [...current, fileUploadItem]);

    if (hasErrors) {
      this.showErrorFiles.set(true);
    } else {
      this.emitFilesChanged();
      if (this.canAutoUpload(fileUploadItem)) {
        this.uploadQueue$.next(fileUploadItem);
      }
    }
  }

  /**
   * Emits the `uploadComplete` output event if all valid files have finished uploading.
   */
  private emitUploadComplete(): void {
    if (this.uploadDone()) {
      const failedItems = this.validFiles().filter(file => file.status() === 'error');
      this.uploadComplete.emit({
        hasErrors: failedItems.length > 0,
        filesWithErrors: failedItems
      });
    }
  }

  /**
   * Emits the `filesChanged` output event with the current list of valid `File` objects.
   */
  private emitFilesChanged(): void {
    const files = this.validFiles().map(file => file.file);
    this.filesChanged.emit(files);
  }

  /**
   * Determines if a file item should be automatically uploaded based on `autoUpload` input.
   * @param item The file upload item to check.
   * @returns `true` if auto-upload is enabled and the item has no validation errors.
   */
  private canAutoUpload( item: FileUploadItem ): boolean {
    return this.autoUpload() && item.errorType() !== 'validation';
  }

  /**
   * Removes a file item from the list after a short delay (typically after a successful upload).
   * Used when `showUploadedFiles` is false.
   * @param item The file upload item to remove.
   */
  private removeFileAfterDelay( item: FileUploadItem ): void {
    of(null).pipe(
      delay(800),
    ).subscribe(() => {
      this.fileItems.update(files => files.filter(file => file !== item));
      this.emitFilesChanged();
    });
  }

  /**
   * Formats a file size in Megabytes into a human-readable string (MB or KB).
   * @param sizeInMB The size in megabytes to format.
   * @returns The formatted size string (e.g., "1.5MB" or "500KB").
   */
  private formatSize( sizeInMB: number ): string {
    if (sizeInMB >= 1) {
      return sizeInMB + 'MB';
    }
    return (sizeInMB * 1024) + 'KB';
  }

  /**
   * Manages the upload process for a single file item.
   * Handles state transitions, progress tracking, and error handling.
   * @param item The file upload item to be processed.
   * @returns An Observable of the upload event or null if the item is not in a terminal state.
   */
  private performUpload( item: FileUploadItem ): Observable<HttpEvent<unknown> | null> {
    if (!['pending', 'error', 'cancelled'].includes(item.status())) {
      return of(null);
    }

    this.prepareItemForUpload(item);

    const upload$ = this.buildUploadRequest(item);

    return upload$.pipe(
      tap(event => this.handleUploadEvent(item, event)),
      tap(event => this.handlePostUploadRemoval(item, event)),
      catchError((error: HttpErrorResponse) => {
        item.status.set('error');
        item.errorKeys.set(['Error uploading']);
        item.errorType.set('upload');
        item.httpError.set(error);
        return of(null);
      }),
      finalize(() => {
        item.cancel$.complete();
      }),
      takeUntil(item.cancel$)
    );
  }

  /**
   * Prepares a file item for the upload process.
   * Resets progress, sets status to 'uploading', and initializes cancellation subject.
   * @param item The file upload item to prepare.
   */
  private prepareItemForUpload( item: FileUploadItem ): void {
    item.status.set('uploading');
    item.progress.set(0);
    item.errorType.set(undefined);
    item.cancel$ = new Subject<void>();
  }

  /**
   * Builds and returns the HTTP upload request Observable for a given file item.
   * @param item The file upload item to upload.
   * @returns An Observable stream of {@link HttpEvent} values (progress and final response).
   * @throws Error When the upload request factory is not defined.
   */
  private buildUploadRequest( item: FileUploadItem ): Observable<HttpEvent<unknown>> {
    const handler = this.uploadRequestFactory();

    if (!handler) {
      throw new Error('Upload handler is not defined');
    }

    const customRequest = handler(item.file);

    return this.fileUploadService.upload(customRequest);
  }

  /**
   * Removes the uploaded item from the list (after a short delay) when:
   * - the upload has completed (HTTP Response event), AND
   * - `showUploadedFiles` is false.
   */
  private handlePostUploadRemoval( item: FileUploadItem, event: HttpEvent<unknown> ): void {
    if (event.type === HttpEventType.Response && !this.showUploadedFiles()) {
      this.removeFileAfterDelay(item);
    }
  }

  /**
   * Updates the file item state based on the HTTP upload event.
   * @param item The file upload item being updated.
   * @param event The HTTP event received from the upload service.
   */
  private handleUploadEvent( item: FileUploadItem, event: HttpEvent<unknown> ): void {
    if (event.type === HttpEventType.UploadProgress) {
      const calculatedProgress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
      item.progress.set(calculatedProgress === 100 ? 99 : calculatedProgress);
    } else if (event.type === HttpEventType.Response) {
      item.status.set('completed');
      item.progress.set(100);
      this.emitFilesChanged();
    }
  }
}
